import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { Box, ClickAwayListener } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSuppliers } from 'api/hooks/useSuppliers';
import { useSurgeons } from 'api/hooks/useSurgeons';
import { DeliveriesRequestFilters, DeliveryStatus, LabeledFilter } from 'api/models/Deliveries';
import { useSurgeonsFilters } from 'hooks/useSurgeonsFilters';
import { useLanguage } from 'context/AppContext';
import useSupplierLabels from 'hooks/useSupplierLabels';
import FiltersMenuInput from './components/FiltersMenuInput';
import FiltersSelected, { FilterNamesMap } from './components/FiltersSelected';
import MenuItem from './components/MenuItem';
import { FiltersBox, FiltersButton, FiltersMenuList } from './SearchFilters.styles';
import { FilterId, FilterKey, Lists } from './types';
import { OrdersFilters, OrderStatus } from 'api/models/Orders';
import { TurnaroundListFilters, TurnaroundStage } from 'api/models/TurnaroundList';
import { useDeliveryContentsOptions } from 'hooks/useDeliveryContentsOptions';
import useStatusTranslations from 'hooks/useStatusTranslations';
import { PAST_SURGERIES_ON } from 'featureFlags';

const DELIVERY_SEARCH_FILTER_STATUSES: DeliveryStatus[] = [
  'ORDERED',
  'OUTBOUND',
  'DELIVERED',
  'RECEIVED',
  'PARTIALLY_CHECKED_IN',
  'CHECKED_IN',
  'PARTIALLY_CHECKED_OUT',
  'RETURNED',
];

const HISTORICAL_DELIVERY_SEARCH_FILTER_STATUSES: DeliveryStatus[] = ['CHECKED_OUT', 'CANCELED', 'DELETED'];

const ORDER_SEARCH_FILTER_STATUSES: OrderStatus[] = [
  //'Default',
  'ORDERED',
  'PARTIALLY_DISPATCHED',
  'OUTBOUND', // DISPATCHED
  'PARTIALLY_DELIVERED',
  'DELIVERED',
  'PARTIALLY_RECEIVED',
  'RECEIVE',
  'PARTIALLY_CHECKED_IN',
  'CHECK_IN',
  'PARTIALLY_CHECKED_OUT',
  'PARTIALLY_RETURNED',
];

const HISTORICAL_ORDER_SEARCH_FILTER_STATUSES: OrderStatus[] = ['CHECK_OUT', 'RETURNED', 'DELETED', 'CANCELED'];

const SearchFilters = <T extends DeliveriesRequestFilters | OrdersFilters | TurnaroundListFilters>({
  filters,
  dataType = 'deliveries',
  isHistorical,
  setFilters,
  enabledFilters = {
    surgeryDate: 'surgery_date',
    dueDate: 'due_date',
    status: 'status',
    supplier: 'supplier',
    surgeon: 'surgeon',
  },
}: {
  filters: T;
  isHistorical?: boolean;
  dataType?: 'deliveries' | 'orders';
  setFilters: (filters: T) => void;
  enabledFilters?: FilterNamesMap;
}) => {
  const [lang] = useLanguage();
  const statusLang = useStatusTranslations();

  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);

  const { data: suppliers } = useSuppliers();

  const [surgeonFilters] = useSurgeonsFilters();

  const { data: surgeons } = useSurgeons({ ...surgeonFilters });

  const supplierLabels = useSupplierLabels({
    suppliers: suppliers?.suppliers,
    flip: true,
  });

  // create the labels for the status filter
  // keep the value so that we can later parse the label back in to the value for the BE
  const searchFilterStatusValueLabels = useMemo(() => {
    let statuses: DeliveryStatus[] | OrderStatus[] = [];
    if (dataType == 'orders') {
      statuses = isHistorical
        ? HISTORICAL_ORDER_SEARCH_FILTER_STATUSES
        : PAST_SURGERIES_ON
        ? ORDER_SEARCH_FILTER_STATUSES
        : ORDER_SEARCH_FILTER_STATUSES.concat(HISTORICAL_ORDER_SEARCH_FILTER_STATUSES);
    } else {
      statuses = isHistorical
        ? HISTORICAL_DELIVERY_SEARCH_FILTER_STATUSES
        : !PAST_SURGERIES_ON
        ? DELIVERY_SEARCH_FILTER_STATUSES
        : DELIVERY_SEARCH_FILTER_STATUSES.concat(HISTORICAL_DELIVERY_SEARCH_FILTER_STATUSES);
    }
    return statuses.map(status => ({ value: status, label: statusLang[status] }));
  }, [dataType, isHistorical, statusLang]);

  const searchFilterDeliveryStatusLabels = useMemo(
    () => searchFilterStatusValueLabels.map(status => status.label),
    [searchFilterStatusValueLabels],
  );

  const turnaroundStageLabels = useMemo(() => {
    const filterableStages: TurnaroundStage[] = ['PENDING', 'COMPLETED'];

    return filterableStages.map(status => lang.TurnaroundStage[status]);
  }, [lang.TurnaroundStage]);

  const deliveryContentOptions = useDeliveryContentsOptions();
  const deliveryContentLabels = useMemo(
    () => deliveryContentOptions.map(content => content.label),
    [deliveryContentOptions],
  );

  const [activeMenuItem, setActivemenuItem] = useState<FilterId | ''>('');

  const activeValue = useMemo<string | undefined>(() => {
    const activeFilterKey = activeMenuItem && enabledFilters[activeMenuItem];
    if (!activeFilterKey) return;

    const filter = filters[activeFilterKey];

    if (activeMenuItem == 'status') {
      const statusLang = { ...lang.OrderStatusText, ...lang.DeliveryStatusText };
      return statusLang[filter];
    }

    return 'string' == typeof filter ? filter : filter?.value;
  }, [activeMenuItem, enabledFilters, filters, lang.DeliveryStatusText, lang.OrderStatusText]);

  const handleMenuItemClick = useCallback((key: FilterId) => {
    setActivemenuItem(key);
  }, []);

  const handleClose = useCallback(() => {
    setMenuOpen(false);
    setActivemenuItem('');
  }, []);

  const handleFilterChange = useCallback(
    (id: FilterId | FilterKey, val: string) => {
      const key = enabledFilters[id] || id;

      // if the id is status, parse the label into the DeliveryStatus
      let value: string | LabeledFilter = val;

      if (id == 'status') {
        // find the matching label and set the value to the DeliveryStatus
        const status = searchFilterStatusValueLabels.find(item => item.label === val);

        value = status?.value || '';
      }

      // if the id is supplier, lookup supplier name from label
      else if (id == 'supplier') {
        const supplierName = supplierLabels[val];
        const supplier = suppliers?.suppliers.find(supplier => supplier.name == value || supplier.name == supplierName);
        value = {
          label: val,
          value: supplierName,
          id: supplier?.tenantId || '',
        };
      }

      setFilters({ ...filters, [key]: value });
      handleClose();
    },
    [
      enabledFilters,
      filters,
      handleClose,
      searchFilterStatusValueLabels,
      setFilters,
      supplierLabels,
      suppliers?.suppliers,
    ],
  );

  const lists: Lists = useMemo(
    () => ({
      supplier: Object.keys(supplierLabels),
      // filter out inactive surgeons and duplicate names
      surgeon: Object.keys(
        (surgeons?.surgeons || []).reduce<Record<string, 1>>((acc, item) => {
          if (item.status) acc[item.name] = 1;
          return acc;
        }, {}),
      ),
      status: searchFilterDeliveryStatusLabels,
      turnaroundStage: turnaroundStageLabels,
      contents: deliveryContentLabels,
    }),
    [
      deliveryContentLabels,
      searchFilterDeliveryStatusLabels,
      supplierLabels,
      surgeons?.surgeons,
      turnaroundStageLabels,
    ],
  );

  const menuItems = useMemo<FilterId[]>(() => {
    let items = Object.keys(enabledFilters) as FilterId[];
    if (lists.supplier.length < 2) items = items.filter(item => item != 'supplier');
    if (lists.surgeon.length < 2) items = items.filter(item => item != 'surgeon');
    return items;
  }, [enabledFilters, lists.supplier.length, lists.surgeon.length]);

  return (
    <Box>
      <FiltersBox>
        <FiltersButton className={isMenuOpen ? 'active' : ''} endIcon={<MoreHoriz />} onClick={() => setMenuOpen(true)}>
          {lang.SearchFilters.buttonLabel}
        </FiltersButton>
        <FiltersSelected<T> filters={filters} enabledFilters={enabledFilters} setFilters={setFilters} />
      </FiltersBox>
      {isMenuOpen && (
        <ClickAwayListener onClickAway={handleClose}>
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'start',
            }}
          >
            <FiltersMenuList>
              {menuItems.map(key => (
                <MenuItem
                  key={key}
                  id={key}
                  isActive={activeMenuItem == key}
                  isHistorical={isHistorical}
                  onClick={handleMenuItemClick}
                />
              ))}
            </FiltersMenuList>
            {!!activeMenuItem && (
              <FiltersMenuInput
                activeMenuItem={activeMenuItem}
                activeValue={activeValue}
                lists={lists}
                onChange={handleFilterChange}
              />
            )}
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default SearchFilters;
